import React, { useMemo, useState } from 'react';
import { format, eachDayOfInterval } from 'date-fns';
import toast from 'react-hot-toast';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    useTheme,
    Box,
    Typography,
    Chip,
    Divider,
    CircularProgress,
} from '@mui/material';
import {
    AccessTime,
    WorkOutline,
    FileDownload,
} from '@mui/icons-material';
import api from 'state/axios';
import { exportEmployeeScheduleToExcel } from './helpers';


const STATUS_COLOR = {
    PUBLISHED: 'success',
    DRAFT:     'default',
    CANCELLED: 'error',
};

const EmployeeScheduleDialog = ({ employee, startDate, endDate, onClose, onPublished, canPublish = false }) => {
    const theme = useTheme();
    const open = !!employee;
    const [publishing, setPublishing] = useState(false);
    const [exporting, setExporting] = useState(false);

    const days = useMemo(() => {
        if (!open || !startDate || !endDate) return [];
        try {
            return eachDayOfInterval({
                start: new Date(startDate),
                end:   new Date(endDate),
            });
        } catch {
            return [];
        }
    }, [open, startDate, endDate]);

    const assignmentsByDate = useMemo(() => {
        const map = {};
        for (const a of employee?.assignments || []) {
            const key = a.date ? a.date.slice(0, 10) : null;
            if (!key) continue;
            if (!map[key]) map[key] = [];
            map[key].push(a);
        }
        return map;
    }, [employee]);

    const emp = useMemo(() => employee?.employee || {}, [employee]);

    const hasDraft = useMemo(() =>
        (employee?.assignments || []).some(a => {
            const ae = a.assignees?.find(x => String(x.employeeId) === String(emp._id));
            return !ae || ae.status === 'DRAFT';
        }),
        [employee, emp]
    );

    const handleExport = async () => {
        if (!employee) return;
        setExporting(true);
        try {
            await exportEmployeeScheduleToExcel({ employee, startDate, endDate });
        } catch {
            toast.error('Export failed.');
        } finally {
            setExporting(false);
        }
    };

    const handlePublish = async () => {
        const employeeId = employee?.employee?._id || employee?.id;
        if (!employeeId) return;
        setPublishing(true);
        try {
            const res = await api.post('/assignments/publish-employee', {
                employeeId,
                startDate,
                endDate,
            });
            toast.success(res.data.message || 'Assignments confirmed.');
            onPublished?.();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Publish failed.');
        } finally {
            setPublishing(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" alignItems="center" gap={1}>
                    {/* <CalendarMonth color="primary" /> */}
                    <Box>
                        <Typography variant="subtitle2" fontWeight="bold" lineHeight={1.2}>
                            {emp.employeeName || employee?.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {emp.employeeCode} · {emp.rank} · {emp.homeBase || emp.location}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 0 }}>
                {days.length === 0 ? (
                    <Box p={3} textAlign="center">
                        <Typography color="text.secondary">No date range selected.</Typography>
                    </Box>
                ) : (
                    <Box>
                        {days.map((day) => {
                            const key = format(day, 'yyyy-MM-dd');
                            const shifts = assignmentsByDate[key] || [];
                            const isToday = key === format(new Date(), 'yyyy-MM-dd');

                            return (
                                <Box
                                    key={key}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                        px: 2,
                                        py: 1,
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        bgcolor: isToday
                                            ? `${theme.palette.primary.main}14`
                                            : 'transparent',
                                    }}
                                >
                                    {/* Date column */}
                                    <Box
                                        sx={{
                                            minWidth: 52,
                                            textAlign: 'center',
                                            pt: 0.25,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            lineHeight={1.2}
                                        >
                                            {format(day, 'EEE')}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontWeight={isToday ? 'bold' : 'normal'}
                                            color={isToday ? 'primary.main' : 'text.primary'}
                                        >
                                            {format(day, 'dd/MM')}
                                        </Typography>
                                    </Box>

                                    {/* Shifts column */}
                                    <Box flex={1}>
                                        {shifts.length === 0 ? (
                                            <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 2 }}>
                                                -
                                            </Typography>
                                        ) : (
                                            <Box display="flex" flexDirection="column" gap={0.5}>
                                                {shifts.map((a) => {
                                                    const shift = a.shiftId || {};
                                                    const ae = a.assignees?.find(x => String(x.employeeId) === String(emp._id));
                                                    const aeStatus = ae?.status || 'DRAFT';
                                                    return (
                                                        <Box
                                                            key={a._id}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                px: 1,
                                                                py: 0.4,
                                                                borderRadius: 1,
                                                                bgcolor: shift.color
                                                                    ? `${shift.color}22`
                                                                    : 'action.hover',
                                                                borderLeft: `3px solid ${shift.color || theme.palette.primary.main}`,
                                                            }}
                                                        >
                                                            <WorkOutline sx={{ fontSize: 14, color: shift.color || 'text.secondary' }} />
                                                            <Typography variant="body2" fontWeight="500" sx={{ flex: 1 }}>
                                                                {shift.shiftName || shift.shiftCode || 'Unknown'}
                                                            </Typography>
                                                            {shift.startTime && (
                                                                <Box display="flex" alignItems="center" gap={0.3}>
                                                                    <AccessTime sx={{ fontSize: 12, color: 'text.secondary' }} />
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {shift.startTime} - {shift.endTime}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            <Chip
                                                                label={aeStatus}
                                                                size="small"
                                                                color={STATUS_COLOR[aeStatus] || 'default'}
                                                                sx={{ height: 18, fontSize: '0.6rem' }}
                                                            />
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    variant="outlined"
                    startIcon={exporting ? <CircularProgress size={14} /> : <FileDownload />}
                    disabled={exporting}
                    onClick={handleExport}
                >
                    Export
                </Button>
                {hasDraft && canPublish && (
                    <Button
                        variant="outlined"
                        color="success"
                        loading={publishing}
                        onClick={handlePublish}
                    >
                        Publish
                    </Button>
                )}
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onClose}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default React.memo(EmployeeScheduleDialog);
